import { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const toDateTimeLocal = (value) => {
    if (!value) {
        return '';
    }
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const iso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    return iso.slice(0, 16);
};

export function EntityModal({ open, title, description, fields, initialValues, onCancel, onSubmit, submitLabel }) {
    const [submitError, setSubmitError] = useState(null);

    if (!open) {
        return null;
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const payload = {};

        fields.forEach((field) => {
            const rawValue = field.type === 'checkbox' ? formData.get(field.name) === 'on' : formData.get(field.name);

            if (field.type === 'checkbox') {
                payload[field.name] = Boolean(rawValue);
                return;
            }

            if (field.type === 'number') {
                if (rawValue === '' || rawValue === null) {
                    if (field.required) {
                        payload[field.name] = null;
                    }
                    return;
                }
                const numericValue = field.numberType === 'float' ? parseFloat(rawValue) : parseInt(rawValue, 10);
                if (!Number.isNaN(numericValue)) {
                    payload[field.name] = numericValue;
                }
                return;
            }

            if (field.type === 'datetime-local') {
                if (!rawValue) {
                    if (field.includeNull) {
                        payload[field.name] = null;
                    }
                    return;
                }
                payload[field.name] = new Date(rawValue).toISOString();
                return;
            }

            if (typeof field.parse === 'function') {
                const parsedValue = field.parse(rawValue);
                if (parsedValue !== undefined) {
                    payload[field.name] = parsedValue;
                }
                return;
            }

            if (rawValue === '' || rawValue === null) {
                if (field.required) {
                    payload[field.name] = rawValue;
                }
                return;
            }

            payload[field.name] = rawValue;
        });

        try {
            await onSubmit(payload);
            setSubmitError(null);
            onCancel();
        } catch (error) {
            setSubmitError(error.message || 'Failed to save changes');
        }
    };

    return createPortal(
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
            <div className="modal" style={{ width: '520px', maxHeight: '80vh', overflowY: 'auto' }}>
                <header className="modal__header">
                    <h3>{title}</h3>
                    <button type="button" className="modal__close" onClick={onCancel} aria-label="Close modal">
                        ×
                    </button>
                </header>
                <form onSubmit={handleSubmit} className="modal__form">
                    {description ? <p className="modal__description">{description}</p> : null}
                    <div className="modal__body" style={{ display: 'grid', gap: '0.75rem' }}>
                        {fields.map((field) => {
                            const defaultValue = initialValues?.[field.name];
                            if (field.type === 'checkbox') {
                                return (
                                    <label key={field.name} className="form-field" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="checkbox"
                                            name={field.name}
                                            defaultChecked={Boolean(defaultValue)}
                                            disabled={field.readOnly}
                                        />
                                        <span>{field.label}</span>
                                    </label>
                                );
                            }

                            if (field.type === 'select') {
                                return (
                                    <label key={field.name} className="form-field">
                                        <span className="form-field__label">{field.label}</span>
                                        <select
                                            name={field.name}
                                            defaultValue={defaultValue ?? ''}
                                            required={field.required}
                                            disabled={field.readOnly}
                                        >
                                            {field.allowEmpty ? <option value="">Select…</option> : null}
                                            {field.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {field.helperText ? <small className="form-field__helper">{field.helperText}</small> : null}
                                    </label>
                                );
                            }

                            if (field.type === 'textarea') {
                                return (
                                    <label key={field.name} className="form-field">
                                        <span className="form-field__label">{field.label}</span>
                                        <textarea
                                            name={field.name}
                                            defaultValue={defaultValue ?? ''}
                                            placeholder={field.placeholder}
                                            required={field.required}
                                            readOnly={field.readOnly}
                                            rows={field.rows || 3}
                                        />
                                        {field.helperText ? <small className="form-field__helper">{field.helperText}</small> : null}
                                    </label>
                                );
                            }

                            const inputProps = {
                                name: field.name,
                                defaultValue:
                                    field.type === 'datetime-local'
                                        ? toDateTimeLocal(defaultValue)
                                        : defaultValue ?? field.defaultValue ?? '',
                                placeholder: field.placeholder,
                                required: field.required,
                                readOnly: field.readOnly,
                                step: field.step,
                                min: field.min,
                                max: field.max,
                            };

                            return (
                                <label key={field.name} className="form-field">
                                    <span className="form-field__label">{field.label}</span>
                                    <input type={field.type || 'text'} {...inputProps} />
                                    {field.helperText ? <small className="form-field__helper">{field.helperText}</small> : null}
                                </label>
                            );
                        })}
                    </div>

                    {submitError ? (
                        <div className="form-error" role="alert">
                            {submitError}
                        </div>
                    ) : null}

                    <footer className="modal__footer">
                        <button type="button" className="secondary-btn" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="primary-btn">
                            {submitLabel || 'Save'}
                        </button>
                    </footer>
                </form>
            </div>
        </div>,
        document.body
    );
}

EntityModal.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.string,
            required: PropTypes.bool,
            readOnly: PropTypes.bool,
            helperText: PropTypes.string,
            placeholder: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    label: PropTypes.string.isRequired,
                })
            ),
        })
    ).isRequired,
    initialValues: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitLabel: PropTypes.string,
};

export default EntityModal;
